import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { Compass, Waves, Sprout, Heart, Users, ArrowRight } from "lucide-react";

export default function About() {
  return (
    <Layout>
      <div data-testid="about-page" className="max-w-4xl mx-auto px-5 md:px-8 py-16 md:py-24 fade-up">
        <span className="overline text-[var(--cm-primary)]">Our origins</span>
        <h1
          data-testid="about-headline"
          className="font-display font-bold mt-3 text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]"
        >
          A village
          <br />
          <span className="italic font-light">becomes a blueprint.</span>
        </h1>

        {/* Founder intro */}
        <section className="mt-12 grid md:grid-cols-5 gap-8 items-start" data-testid="founder-intro">
          <div className="md:col-span-2">
            <div
              data-testid="founder-photo"
              className="aspect-[4/5] rounded-2xl overflow-hidden bg-[var(--cm-accent)]/40 border border-ink-soft"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_cause-match-3/artifacts/h7qsudnw_MDP07400.jpg"
                alt="A diver swimming between coral formations on the Bahoi reef"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          <div className="md:col-span-3">
            <span className="overline text-[var(--cm-primary)]" data-testid="founder-title">Founder &amp; Social Innovation Strategist</span>
            <h2
              data-testid="founder-name"
              className="font-display font-bold text-3xl md:text-4xl tracking-tight mt-1"
            >
              Gin DeMaio
            </h2>
            <p className="text-ink-soft mt-4 leading-relaxed">
              On a trip through Indonesia, I came across a small village whose way of life I
              haven't been able to stop thinking about since.
            </p>
            <p className="text-ink-soft mt-3 leading-relaxed">
              <span className="whitespace-nowrap">Bahoi · CauseMatch</span> is what came of that.
            </p>
          </div>
        </section>

        {/* Story */}
        <article className="prose prose-lg max-w-none mt-14 text-ink leading-relaxed">
          <p className="text-lg md:text-xl text-ink-soft" data-testid="about-intro">
            <span className="text-ink font-semibold">Bahoi</span> sits on the northern coast of
            Sulawesi. By most economic measures, it doesn't have much. What it has is a stretch of
            coral reef, a community that takes care of it, and a remarkable willingness to share
            what little it has — together.
          </p>

          <p className="text-lg leading-relaxed mt-6">
            When I arrived, the village was in the middle of something ambitious: building a
            shared scuba diving experience for visitors, designed so the income could lift the
            whole community. No one household could fund it alone — so every family contributed
            what they had. Boats and motors from one home, cooking and hosting from another, dive
            guides who knew the reef intimately, fishermen who could read the currents, kids who
            helped carry gear down to the shore.
          </p>

          <p className="text-lg leading-relaxed mt-4">
            It wasn't one entrepreneur or one NGO building this — it was the whole village
            agreeing to build something only the whole village could build. Here's how that came
            together:
          </p>

          <div className="grid md:grid-cols-3 gap-4 my-10 not-prose">
            <StoryCard
              icon={Waves}
              color="#2C5F7C"
              title="The reef"
              body="One of the healthiest coral reefs in the region — protected for decades by the villagers themselves through traditional sasi practices."
            />
            <StoryCard
              icon={Users}
              color="#D96C4A"
              title="The pooling"
              body="When the village decided to offer a scuba diving experience to visitors, no single household could fund it. So everyone contributed what they had — boats, time, hosting, fish, knowledge."
            />
            <StoryCard
              icon={Heart}
              color="#4A6B53"
              title="The lift"
              body="The income from that single shared experience supports the whole village — schools, clinic, and a community fund that keeps the reef under their care."
            />
          </div>

          <p className="text-lg leading-relaxed">
            What I saw in Bahoi wasn't a charity project or an NGO. It was an economy of
            generosity. A whole community pooling time, skill, and care into something larger than
            any of them — and then sharing what came back.
          </p>
        </article>

        {/* Team vision */}
        <section className="mt-16" data-testid="team-vision">
          <span className="overline text-[var(--cm-primary)]">From a village to a team</span>
          <h2 className="font-display font-bold text-3xl md:text-4xl mt-2 tracking-tight">
            Our vision.
          </h2>

          <div className="mt-6 space-y-5 text-ink leading-relaxed">
            <p>
              After coming home, our team gathered around one shared dream: to make purposeful
              work <span className="font-semibold">easily accessible and recognized</span>. To
              honor people for what they contribute to their community — not for how much money
              they bring in.
            </p>
            <p>
              Here's the tension we kept running into: people genuinely want to do good. They also
              have rent to pay. Most of us spend our days choosing between work that pays and work
              that matters — and "work that matters" usually loses. Bahoi · CauseMatch exists to
              close that gap. When you give your time here, you don't just feel good about it —
              you're <span className="font-semibold">reciprocated</span> for it, in a currency that
              flows back into your own life through the marketplace.
            </p>
            <p>
              We learned something else along the way. In conversation after conversation, people
              told us they'd love to get involved if they only knew where to start. That's why we
              built the assessment — because when a person is{" "}
              <span className="font-semibold">emotionally aligned</span> with what they're doing,
              they're more likely to show up in a way that genuinely serves the people on the
              other side of it.
            </p>
            <p>
              When the heart is in the work, both giver and receiver benefit greatly — whether
              that's a neighbor down the block, an organization across town, or a village across
              the ocean.
            </p>
          </div>
        </section>

        {/* Bridge: That's the platform */}
        <section className="mt-16">
          <h2 className="font-display font-bold text-3xl md:text-4xl mb-4 tracking-tight">
            And so we built this.
          </h2>
          <p className="text-ink leading-relaxed">
            <span className="font-semibold">Bahoi · CauseMatch</span> is a digital expression of
            what I experienced in that village — translated into a platform where:
          </p>
          <ul className="list-none p-0 my-6 space-y-3">
            <Bullet>
              A community can keep track of the <strong>time and energy</strong> its members are
              actually contributing — every hour counts, every shift verified.
            </Bullet>
            <Bullet>
              Anyone can <strong>offer what they have</strong> — a skill, a meal, a lesson, a
              service, a piece of work made with their hands — and price it in Time Hours.
            </Bullet>
            <Bullet>
              Others can <strong>spend the hours they earned</strong> on what their neighbors offer
              — including, eventually, experiences as singular as a scuba dive in a village that
              built it together.
            </Bullet>
          </ul>
          <p className="text-ink leading-relaxed">
            We borrow Bahoi's name because we borrow Bahoi's idea: that a community's collective
            time is the most honest currency it has, and that when you pool it well, even the
            smallest place can build something the whole world will want to be part of. And so
            we're giving the name a meaning it didn't have before —{" "}
            <span className="font-semibold">supportive community</span>, the kind there hasn't
            quite been a word for.
          </p>
        </section>

        {/* Three pillars */}
        <section className="mt-16">
          <span className="overline text-[var(--cm-primary)]">What we believe</span>
          <div className="grid md:grid-cols-3 gap-6 mt-6">
            <Pillar
              title="Time, honestly counted."
              body="No volunteer hour disappears into thin air. Organizers verify. Hours are credited. A ledger keeps it all visible."
            />
            <Pillar
              title="Causes you actually care about."
              body="Our 18-question assessment matches you to 1 of 8 cause areas. Not by job title, by how you see the world."
            />
            <Pillar
              title="A marketplace, not a charity."
              body="Time Hours can be spent. You give an hour, you can receive an hour. Dignity goes both ways."
            />
          </div>
        </section>

        {/* Dictionary-style definition (moved to bottom) */}
        <section className="mt-20">
          <span className="overline text-[var(--cm-primary)]">And so we call it</span>
          <div
            data-testid="bahoi-definition"
            className="mt-3 card-soft p-6 md:p-8 border-l-4"
            style={{ borderLeftColor: "var(--cm-primary)" }}
          >
            <div className="flex items-baseline flex-wrap gap-x-4 gap-y-1">
              <span className="font-display font-bold text-3xl md:text-4xl tracking-tight">
                Bahoi
              </span>
              <span className="text-ink-soft text-base italic">/ bə-HOY /</span>
              <span className="overline text-ink-soft">noun</span>
            </div>
            <p className="font-display text-xl md:text-2xl mt-3 leading-snug">
              <span className="text-[var(--cm-primary)] font-semibold">Supportive Community</span>
            </p>
            <p className="text-sm text-ink-soft mt-3 leading-relaxed italic">
              Not actually a word in Indonesian — it's the name of a village.
              <br />
              "Supportive Community" is our adaptation, based on what was experienced there.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="mt-12">
          <div
            data-testid="about-cta"
            className="rounded-3xl bg-[var(--cm-ink)] text-[var(--cm-surface)] px-8 md:px-12 py-12 md:py-14 flex flex-wrap gap-6 items-center justify-between"
          >
            <div className="max-w-md">
              <Compass className="h-5 w-5 mb-3 text-[var(--cm-accent)]" />
              <h3 className="font-display font-bold text-2xl md:text-3xl tracking-tight">
                Bring your community to Bahoi.
              </h3>
              <p className="text-white/70 mt-2 text-sm">
                Whether you're a village, a neighborhood, a club, or a continent — the same model
                works. Start by joining.
              </p>
            </div>
            <Link
              to="/register"
              data-testid="about-cta-button"
              className="btn-primary inline-flex items-center gap-2 text-lg px-8 py-4"
            >
              Take the assessment <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </section>
      </div>
    </Layout>
  );
}

function StoryCard({ icon: Icon, title, body, color }) {
  return (
    <div className="card-soft p-5">
      <span
        className="h-10 w-10 rounded-full grid place-items-center text-white"
        style={{ background: color }}
      >
        <Icon className="h-5 w-5" />
      </span>
      <h3 className="font-display font-semibold mt-3 leading-tight">{title}</h3>
      <p className="text-sm text-ink-soft mt-2 leading-relaxed">{body}</p>
    </div>
  );
}

function Bullet({ children }) {
  return (
    <li className="flex items-start gap-3">
      <span className="h-6 w-6 rounded-full bg-[var(--cm-secondary)] text-white grid place-items-center flex-shrink-0 mt-0.5">
        <Sprout className="h-3 w-3" />
      </span>
      <span className="text-ink leading-relaxed">{children}</span>
    </li>
  );
}

function Pillar({ title, body }) {
  return (
    <div className="card-soft p-6">
      <h3 className="font-display font-semibold text-lg">{title}</h3>
      <p className="text-sm text-ink-soft mt-2 leading-relaxed">{body}</p>
    </div>
  );
}
